import { takeLatest, put, call, select } from 'redux-saga/effects';
import {
  ActivateUserPayload,
  AuthAPI,
  ChangePasswordPayload,
  ProfileUpdatePayload,
  ClinicUpdatePayload,
  ProfileClinicUpdatePayload,
} from '../../api';
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  registerRequest,
  registerSuccess,
  registerFailure,
  googleLoginRequest,
  googleLoginSuccess,
  googleLoginFailure,
  refreshTokenRequest,
  refreshTokenSuccess,
  refreshTokenFailure,
  activateUserFailure,
  activateUserSuccess,
  activateUserRequest,
  changePasswordRequest,
  changePasswordSuccess,
  changePasswordFailure,
  updateProfileRequest,
  updateProfileSuccess,
  updateProfileFailure,
  updateClinicRequest,
  updateClinicSuccess,
  updateClinicFailure,
  updateProfileAndClinicRequest,
  updateProfileAndClinicSuccess,
  updateProfileAndClinicFailure,
  verifyEmailRequest,
  verifyEmailSuccess,
  verifyEmailFailure,
  resendCodeRequest,
  resendCodeSuccess,
  resendCodeFailure,
  setupClinicRequest,
  setupClinicSuccess,
  setupClinicFailure,
} from '../slices/authSlice';
import { getErrorMessage } from '../../utils/errorParser';

// Import types from auth service
type LoginResponse = {
  user: any;
  access: string;
  refresh: string;
  redirect_uri: string;
};

type UserData = {
  id: string;
  email: string;
  title?: string;
  first_name: string;
  last_name: string;
  phone?: string;
  address?: string;
  dob?: string;
  user_role: string;
  communication_preference?: string;
  verified?: boolean;
  clinic?: {
    id: number;
    name: string;
    abn: string;
    address: string;
    specialty: string;
  } | null;
};
interface LoginPayload {
  email: string;
  password: string;
}

interface RegisterPayload {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
}

interface GoogleLoginPayload {
  credential: string;
}

// Login saga
function* handleLogin(action: ReturnType<typeof loginRequest>): Generator {
  try {
    const payload = action.payload as unknown as LoginPayload;
    const { email, password } = payload;
    const response: LoginResponse = yield call(AuthAPI.login, email, password);
    yield put(
      loginSuccess({
        user: response.user,
        tokens: { access: response.access, refresh: response.refresh },
      })
    );
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(loginFailure(errorMessage));
  }
}

// Register saga
function* handleRegister(action: ReturnType<typeof registerRequest>): Generator {
  try {
    const payload = action.payload as unknown as RegisterPayload & { country?: string };
    const { first_name, last_name, email, password, confirm_password } = payload;
    const response: { message?: string; error?: string } = yield call(AuthAPI.register, {
      first_name,
      last_name,
      email,
      password,
      confirm_password,
      user_role: 'Doctor',
    });

    if (response.error) {
      const errorMessage = getErrorMessage({
        response: { data: { error: response.error }, status: 400 },
      });
      yield put(registerFailure(errorMessage));
      return;
    }

    yield put(registerSuccess({ message: response.message, email, password }));
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(registerFailure(errorMessage));
  }
}

// Email verification saga
function* handleVerifyEmail(action: ReturnType<typeof verifyEmailRequest>): Generator {
  console.log('🔍 handleVerifyEmail saga started with action:', action);
  try {
    const { email, code } = action.payload;
    console.log('Starting email verification for:', email);

    const response: {
      message: string;
      access_token: string;
      refresh_token: string;
      user: UserData;
    } = yield call(AuthAPI.verifyEmailCode, email, code);
    console.log('Email verification response:', response);

    // Email verification successful with authentication data
    yield put(
      verifyEmailSuccess({
        message: response.message,
        user: response.user,
        tokens: {
          access: response.access_token,
          refresh: response.refresh_token,
        },
      })
    );

    console.log('Email verified and user authenticated successfully');
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(verifyEmailFailure(errorMessage));
  }
}

// Resend code saga
function* handleResendCode(action: ReturnType<typeof resendCodeRequest>): Generator {
  try {
    const { email } = action.payload;
    const response: { message: string } = yield call(AuthAPI.resendVerificationCode, email);
    yield put(resendCodeSuccess(response));
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(resendCodeFailure(errorMessage));
  }
}

// Setup clinic saga
function* handleSetupClinic(action: ReturnType<typeof setupClinicRequest>): Generator {
  try {
    const {
      field_of_practice,
      business_name,
      practice_management_software,
      location,
      is_practice_manager,
    } = action.payload;

    // Call the new single API endpoint
    const response: {
      message: string;
      doctor: any;
      clinic: any;
      is_practice_manager: boolean;
    } = yield call(AuthAPI.setupDoctor, {
      field_of_practice,
      business_name,
      practice_management_software,
      location,
      is_practice_manager,
    });

    console.log('Doctor setup successful:', response);
    yield put(setupClinicSuccess());
  } catch (error: any) {
    console.error('Setup clinic error:', error);
    const errorMessage = getErrorMessage(error);
    yield put(setupClinicFailure(errorMessage));
  }
}

// Google Login saga
function* handleGoogleLogin(action: ReturnType<typeof googleLoginRequest>): Generator {
  try {
    const payload = action.payload as unknown as GoogleLoginPayload;
    const { credential } = payload;
    const response: LoginResponse = yield call(AuthAPI.googleLogin, credential);
    yield put(googleLoginSuccess(response));
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(googleLoginFailure(errorMessage));
  }
}

// Refresh Token saga
function* handleRefreshToken(): Generator {
  try {
    // Get the refresh token from the Redux state
    const state: any = yield select();
    const refreshToken = state.auth.tokens?.refresh;

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = yield call(AuthAPI.refreshToken, refreshToken);
    yield put(refreshTokenSuccess(response));
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(refreshTokenFailure(errorMessage));
  }
}

// Activate User saga
function* handleActivateUser(action: ReturnType<typeof activateUserRequest>): Generator {
  try {
    const payload = action.payload as unknown as ActivateUserPayload;
    const { uid, token } = payload;
    yield call(AuthAPI.activateUser, { uid, token });
    yield put(activateUserSuccess());
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(activateUserFailure(errorMessage));
  }
}

// Change Password saga
function* handleChangePassword(action: ReturnType<typeof changePasswordRequest>): Generator {
  try {
    const payload = action.payload as unknown as ChangePasswordPayload;
    const { current_password, new_password, confirm_password } = payload;
    yield call(AuthAPI.changePassword, {
      current_password,
      new_password,
      confirm_password,
    });
    yield put(changePasswordSuccess());
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(changePasswordFailure(errorMessage));
  }
}

// Profile Update saga
function* handleUpdateProfile(action: ReturnType<typeof updateProfileRequest>): Generator {
  try {
    console.log('Saga: handleUpdateProfile called with payload:', action.payload);
    const payload = action.payload as unknown as ProfileUpdatePayload;
    console.log('Saga: Calling AuthAPI.updateProfile with payload:', payload);
    const response = yield call(AuthAPI.updateProfile, payload);
    console.log('Saga: API response received:', response);
    yield put(updateProfileSuccess(response.user));
  } catch (error: any) {
    console.error('Saga: Error in handleUpdateProfile:', error);
    const errorMessage = getErrorMessage(error);
    console.log('Saga: Dispatching error:', errorMessage);
    yield put(updateProfileFailure(errorMessage));
  }
}

// Clinic Update saga
function* handleUpdateClinic(action: ReturnType<typeof updateClinicRequest>): Generator {
  try {
    console.log('Saga: handleUpdateClinic called with payload:', action.payload);
    const payload = action.payload as unknown as ClinicUpdatePayload;
    console.log('Saga: Calling AuthAPI.updateClinic with payload:', payload);
    const response = yield call(AuthAPI.updateClinic, payload);
    console.log('Saga: API response received:', response);
    yield put(updateClinicSuccess(response.user));
  } catch (error: any) {
    console.error('Saga: Error in handleUpdateClinic:', error);
    const errorMessage = getErrorMessage(error);
    console.log('Saga: Dispatching error:', errorMessage);
    yield put(updateClinicFailure(errorMessage));
  }
}

// Profile and Clinic Combined Update saga
function* handleUpdateProfileAndClinic(
  action: ReturnType<typeof updateProfileAndClinicRequest>
): Generator {
  try {
    console.log('Saga: handleUpdateProfileAndClinic called with payload:', action.payload);
    const payload = action.payload as unknown as ProfileClinicUpdatePayload;
    console.log('Saga: Calling AuthAPI.updateProfileAndClinic with payload:', payload);
    const response = yield call(AuthAPI.updateProfileAndClinic, payload);
    console.log('Saga: API response received:', response);
    yield put(updateProfileAndClinicSuccess(response.user));
  } catch (error: any) {
    console.error('Saga: Error in handleUpdateProfileAndClinic:', error);
    const errorMessage = getErrorMessage(error);
    console.log('Saga: Dispatching error:', errorMessage);
    yield put(updateProfileAndClinicFailure(errorMessage));
  }
}

// Root saga
export function* authSaga() {
  yield takeLatest(loginRequest.type, handleLogin);
  yield takeLatest(registerRequest.type, handleRegister);
  yield takeLatest(verifyEmailRequest.type, handleVerifyEmail);
  yield takeLatest(resendCodeRequest.type, handleResendCode);
  yield takeLatest(setupClinicRequest.type, handleSetupClinic);
  yield takeLatest(googleLoginRequest.type, handleGoogleLogin);
  yield takeLatest(refreshTokenRequest.type, handleRefreshToken);
  yield takeLatest(activateUserRequest.type, handleActivateUser);
  yield takeLatest(changePasswordRequest.type, handleChangePassword);
  yield takeLatest(updateProfileRequest.type, handleUpdateProfile);
  yield takeLatest(updateClinicRequest.type, handleUpdateClinic);
  yield takeLatest(updateProfileAndClinicRequest.type, handleUpdateProfileAndClinic);
}
